import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Mail,
  MessageSquare,
} from 'lucide-react';

import { useLanguage } from 'shared/context/LanguageContext';
import { ltrIsolate } from 'shared/lib/bidi';
import type { OtpMethod } from '../types';

interface ChannelStepProps {
  selected: OtpMethod;
  availableMethods: OtpMethod[];
  maskedPhone?: string;
  maskedEmail?: string;
  isSubmitting: boolean;
  errorMessage: string | null;
  onSelect: (method: OtpMethod) => void;
  onBack: () => void;
  onContinue: () => void;
}

const ChannelStep: React.FC<ChannelStepProps> = ({
  selected,
  availableMethods,
  maskedPhone,
  maskedEmail,
  isSubmitting,
  errorMessage,
  onSelect,
  onBack,
  onContinue,
}) => {
  const { t } = useTranslation();
  const { dir } = useLanguage();
  const Forward = dir === 'rtl' ? ArrowLeft : ArrowRight;
  const Back = dir === 'rtl' ? ArrowRight : ArrowLeft;

  const options: Array<{
    method: OtpMethod;
    Icon: React.FC<{ className?: string }>;
    titleKey: string;
    hint: string;
  }> = [];

  if (availableMethods.includes('sms')) {
    options.push({
      method: 'sms',
      Icon: MessageSquare,
      titleKey: 'partnerAuth.channel.sms',
      hint: t('partnerAuth.channel.smsHint', {
        target: maskedPhone
          ? ltrIsolate(maskedPhone)
          : t('partnerAuth.channel.yourPhone'),
      }),
    });
  }
  if (availableMethods.includes('email')) {
    options.push({
      method: 'email',
      Icon: Mail,
      titleKey: 'partnerAuth.channel.email',
      hint: t('partnerAuth.channel.emailHint', {
        target: maskedEmail
          ? ltrIsolate(maskedEmail)
          : t('partnerAuth.channel.yourEmail'),
      }),
    });
  }

  return (
    <div className="space-y-5">
      <div className="text-center mb-2">
        <h2 className="text-2xl font-bold text-gray-900">
          {t('partnerAuth.channel.heading')}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {t('partnerAuth.channel.subheading')}
        </p>
      </div>

      <div className="space-y-3">
        {options.map(({ method, Icon, titleKey, hint }) => {
          const isSelected = selected === method;
          return (
            <motion.button
              key={method}
              type="button"
              onClick={() => onSelect(method)}
              disabled={isSubmitting}
              whileHover={!isSubmitting ? { scale: 1.015 } : undefined}
              whileTap={!isSubmitting ? { scale: 0.99 } : undefined}
              className={`w-full text-start rounded-2xl border-2 p-4 transition-all flex items-center gap-4 disabled:opacity-60 disabled:cursor-not-allowed ${
                isSelected
                  ? 'border-haat-red-500 bg-haat-red-50/40 shadow-md shadow-haat-red-100'
                  : 'border-gray-200 bg-white hover:border-haat-red-300'
              }`}
            >
              <span
                className={`flex h-12 w-12 items-center justify-center rounded-xl ${
                  isSelected
                    ? 'bg-haat-red-500 text-white'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                <Icon className="h-6 w-6" />
              </span>
              <span className="flex-1">
                <span className="block font-semibold text-gray-900">
                  {t(titleKey)}
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  {hint}
                </span>
              </span>
              <span
                className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                  isSelected
                    ? 'border-haat-red-500 bg-haat-red-500'
                    : 'border-gray-300'
                }`}
              >
                {isSelected && (
                  <span className="h-2 w-2 rounded-full bg-white" />
                )}
              </span>
            </motion.button>
          );
        })}
      </div>

      {errorMessage && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700"
        >
          {errorMessage}
        </motion.div>
      )}

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="flex-1 rounded-2xl border-2 border-gray-200 bg-white text-gray-700 font-semibold py-3 flex items-center justify-center gap-2 hover:bg-gray-50 disabled:opacity-50"
        >
          <Back className="h-4 w-4" />
          {t('common.back')}
        </button>

        <motion.button
          type="button"
          onClick={onContinue}
          disabled={isSubmitting || options.length === 0}
          whileHover={!isSubmitting ? { scale: 1.01 } : undefined}
          whileTap={!isSubmitting ? { scale: 0.98 } : undefined}
          className="flex-[2] rounded-2xl bg-gradient-to-r from-haat-red-500 to-haat-red-700 text-white font-semibold py-3 flex items-center justify-center gap-2 shadow-lg shadow-haat-red-200/60 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              {t('partnerAuth.channel.continue')}
              <Forward className="h-4 w-4" />
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
};

export default ChannelStep;
